/**
 * Client → product-feedback bot payload. Identity fields are stripped/ignored;
 * the bot reads the session profile. Recipients are hardcoded in the bot.
 */
import { z } from 'zod';

export const MAX_FEEDBACK_CHARS = 8000;
/** Raw image cap. 3 MiB base64-fits under AWS Lambda's 6 MiB sync invoke limit. */
export const MAX_FEEDBACK_SCREENSHOT_BYTES = 3 * 1024 * 1024;
/** Encoded screenshot payload cap (base64 characters, no data-URL prefix). */
export const MAX_FEEDBACK_SCREENSHOT_BASE64_CHARS =
  Math.ceil((MAX_FEEDBACK_SCREENSHOT_BYTES * 4) / 3) + 8;

export const FEEDBACK_SCREENSHOT_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;

const PAGE_PATH_RE = /^\/[^?#]*$/;

export const productFeedbackScreenshotSchema = z.object({
  dataBase64: z.string().min(1).max(MAX_FEEDBACK_SCREENSHOT_BASE64_CHARS),
  mimeType: z.enum(FEEDBACK_SCREENSHOT_MIME_TYPES),
  filename: z.string().trim().min(1).max(200),
});

export const productFeedbackInputSchema = z
  .object({
    feedback: z.string().trim().min(1).max(MAX_FEEDBACK_CHARS),
    pagePath: z.string().max(200).regex(PAGE_PATH_RE, 'Must be a path starting with /'),
    environment: z.enum(['LOCAL', 'DEV', 'PROD']),
    screenshot: productFeedbackScreenshotSchema.optional(),
  })
  .strip();

export type ProductFeedbackInput = z.infer<typeof productFeedbackInputSchema>;
